import React, { useEffect, useRef, useState } from "react";

// Modules

// App
import { filesystem_root, subdomain } from "../../../config";
import { jsonParser } from "../../../utils/helpers";

// UI components

export default function Thumbnail(props) {
  const [isError, setError] = useState(false);

  const isLibrarySlide =
    props.slide.initial_presentation === props.presentation.id ? false : true;
  const rootThumb = isLibrarySlide
    ? `${filesystem_root}/${subdomain}/presentations/${props.slide.initial_presentation}/${props.slide.initial_presentation}/thumbnails/slide_${props.slide.id}_thumbnail.jpg`
    : `${filesystem_root}/${subdomain}/slides/${props.slide.id}/slide_${props.slide.id}_thumbnail.jpg`;
  const [thumbnail, setThumbnail] = useState(`${rootThumb}?t=${Date.now()}`);
  const [isActive, setActive] = useState(
    isLibrarySlide ? false : props.isActive
  );

  // Poll every 5s if error, up to 10 times, then 1min cooldown, max 3 cycles
  // This should prevent spamming the server indefinitely if there's a persistent issue, while still trying to recover if it's a transient problem

  const [errorCount, setErrorCount] = useState(0);
  const [isCooldown, setIsCooldown] = useState(false);
  const [cycleCount, setCycleCount] = useState(0);
  const timeoutRef = useRef(null);
  const MAX_ERRORS_PER_CYCLE = 10;
  const MAX_CYCLES = 3;

  useEffect(() => {
    if (props.isActive === true) {
      onItemClicked();
      setActive(true);
    } else {
      setActive(false);
      onItemNavigatedAway();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isActive]);

  useEffect(() => {
    if (!isActive || !props.isSlide || !props.slide || !isError) return;
    if (cycleCount >= MAX_CYCLES) return; // Stop after max cycles

    if (isCooldown) {
      // 1 minute break before next cycle
      timeoutRef.current = setTimeout(() => {
        setIsCooldown(false);
        setErrorCount(0);
        setThumbnail(`${rootThumb}?t=${Date.now()}`);
      }, 60000);
    } else if (errorCount < MAX_ERRORS_PER_CYCLE) {
      timeoutRef.current = setTimeout(() => {
        setThumbnail(`${rootThumb}?t=${Date.now()}`);
      }, 5000);
    } else {
      // Hit 10 errors, start cooldown and increment cycle
      setCycleCount((prev) => prev + 1);
      setIsCooldown(true);
    }

    return () => clearTimeout(timeoutRef.current);
  }, [isError, errorCount, isCooldown, cycleCount, isActive, props.isSlide, props.slide, rootThumb]);

  // Load initial thumbnail when becoming active
  useEffect(() => {
    if (isActive && props.isSlide && props.slide) {
      const json = jsonParser(props.slide.slide_json);

      if (json?.reveal?.slideThumbnail?.field_media_image) {
        setThumbnail(json.reveal.slideThumbnail.field_media_image);
      } else {
        setThumbnail(`${rootThumb}?t=${Date.now()}`);
      }
    }

    return () => clearTimeout(timeoutRef.current);
  }, [isActive, props.isSlide, props.slide, rootThumb]);

  // Auto-refresh interval for sidebar thumbnails
  useEffect(() => {
    if (!props.autoRefreshInterval || props.autoRefreshInterval <= 0) return;

    const intervalId = setInterval(() => {
      setThumbnail(`${rootThumb}?t=${Date.now()}`);
      setError(false); // Reset error state so <img> renders and can attempt to load the new URL
    }, props.autoRefreshInterval);

    return () => clearInterval(intervalId);
  }, [props.autoRefreshInterval, rootThumb]);

  const onItemClicked = () => {
    if (props.isSlide && props.slide) {
      const json = jsonParser(props.slide.slide_json);

      // has bespoke
      if (json?.reveal?.slideThumbnail?.field_media_image) {
        setThumbnail(json.reveal.slideThumbnail?.field_media_image);
      } else {
        setThumbnail(`${rootThumb}?t=${Date.now()}`);
      }
    }
  };

  const onItemNavigatedAway = () => {
    setTimeout(() => {
      if (props.isSlide && props.slide) {
        const json = jsonParser(props.slide.slide_json);

        // has bespoke
        if (json?.reveal?.slideThumbnail?.field_media_image) {
          setThumbnail(json.reveal.slideThumbnail?.field_media_image);
        } else {
          setThumbnail(`${rootThumb}?t=${Date.now()}`);
        }
      }
    }, 30000);
  };

  return (
    <>
      {isError ? (
        <div className="no-thumb" data-thumb={thumbnail} />
      ) : (
        <img
          src={thumbnail}
          alt=""
          onLoad={() => {
            setError(false);
            setErrorCount(0);
            setIsCooldown(false);
            setCycleCount(0);
          }}
          onError={() => {
            setError(true);
            setErrorCount((prev) => prev + 1);
          }}
        />
      )}
    </>
  );
}
